// @ts-strict-ignore
import { getProperty, setProperty } from 'dot-prop';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import type {
  BooleanRule, BooleanRuleArray, IndexedRule, LineRule, RuleObject,
} from '../../types';
import { LineRuleTypes } from '../../types';
import { getOperatorUsedInRuleObject, getPathArrayAndParentPathString, getPureNumberFromPartialPath } from '../helpers';
import { createStringOfPathArray } from '../../utils';
import { booleanRuleStrings } from '../../constants';


export function reorderRuleItem({
  draggedLineRule,
  dropTargetLineRule,
  sourceActionRules,
}: {
  draggedLineRule: LineRule;
  dropTargetLineRule: LineRule;
  sourceActionRules: RuleObject;
}) {
  const {
    parentPathString: draggedLineParentPath,
    lastPathItem: draggedLineLastPathItem,
    pathAsArray: draggedLineParentPathArr,
  } = getPathArrayAndParentPathString({
    pathString: draggedLineRule.path,
    parentLevel: 1,
  });

  const dropTargetParentData = getPathArrayAndParentPathString({
    pathString: dropTargetLineRule.path,
    parentLevel: 1,
  });
  const {
    parentPathString: dropTargetLineParentPath,
    pathAsArray: dropTargetLineParentPathArr,
  } = dropTargetParentData;
  let {
    lastPathItem: dropTargetLineLastPathItem,
  } = dropTargetParentData;

  if (isNaN(dropTargetLineLastPathItem)) {
    // last path item was and / or - in this case, we need to go one more parent level (the path string) to get the "location"
    const {
      pathAsArray: dropTargetLineDirectParentPathArray,
    } = getPathArrayAndParentPathString({
      pathString: dropTargetLineRule.path,
      parentLevel: 1,
    });

    dropTargetLineLastPathItem = getPureNumberFromPartialPath(
      dropTargetLineDirectParentPathArray.at(-1));
  } else {
    dropTargetLineLastPathItem += 1;
  }

  const originalDraggedLineParentRule = cloneDeep(
    getProperty(
      sourceActionRules,
      draggedLineParentPath) as BooleanRuleArray,
  );

  if (draggedLineRule.type === LineRuleTypes.THRESHOLD) {
    // first check the parent path of both to see if they are in a same parent ruleset
    // the parent rule will always be array when both are threshold
    const parentRule = getProperty(
      sourceActionRules,
      dropTargetLineParentPath,
    ) as BooleanRuleArray | IndexedRule | BooleanRule;

    let updatedParentRule;
    if (!Array.isArray(parentRule)) {
      // it means user dropped this item to the first line of a group (to be the first item in there)
      const ruleOperator = getOperatorUsedInRuleObject({ rule: parentRule });
      if (!ruleOperator) return sourceActionRules;

      updatedParentRule = [
        { id: draggedLineRule.value },
        ...parentRule[ruleOperator],
      ];

      setProperty(
        sourceActionRules,
        `${dropTargetLineParentPath}.${ruleOperator}`,
        updatedParentRule,
      );
    } else {
      if (Number(dropTargetLineLastPathItem) === 0) {
        // user dropped the item to be the first item in wherever that scope
        updatedParentRule = [
          { id: draggedLineRule.value },
          ...parentRule.slice(0, Number(dropTargetLineLastPathItem) + 1),
          ...parentRule.slice(Number(dropTargetLineLastPathItem) + 1),
        ];
      } else {
        updatedParentRule = [
          ...parentRule.slice(0, Number(dropTargetLineLastPathItem)),
          { id: draggedLineRule.value },
          ...parentRule.slice(Number(dropTargetLineLastPathItem)),
        ];
      }

      setProperty(
        sourceActionRules,
        dropTargetLineParentPath,
        updatedParentRule,
      );
    }

    let draggedLineParentRule = getProperty(
      sourceActionRules, draggedLineParentPath) as BooleanRuleArray;

    const originalDropTargetLineEndsWithOperator = (
      dropTargetLineRule.path.endsWith(booleanRuleStrings.AND_OPERATOR_STRING)
      || dropTargetLineRule.path.endsWith(booleanRuleStrings.OR_OPERATOR_STRING)
    );

    const isItemDroppedInTheFirstIndexOfGroup = draggedLineParentPathArr[
      dropTargetLineParentPathArr.length]
    && [
      booleanRuleStrings.AND_OPERATOR_STRING,
      booleanRuleStrings.OR_OPERATOR_STRING,
    ].includes(draggedLineParentPathArr[dropTargetLineParentPathArr.length])
    && originalDropTargetLineEndsWithOperator;

    const parentLevelPathKey = draggedLineParentPathArr.length > 2
      ? draggedLineParentPathArr.slice(0, draggedLineParentPathArr.length - 2)
      : draggedLineParentPathArr[0];
    if ((parentLevelPathKey.at(-1) === dropTargetLineParentPathArr.at(-1))
      || draggedLineRule.path.includes(dropTargetLineParentPath)
    ) {
      // if these two are same, it means as a result of the data update in the lines above this, (updatedParentRule)
      // the parent level data has been updated and resulted in indices shift (say or ruleset's length is now 4 instead of 3)
      // this means "the index we'll be using later for update the parent ruleset of the dragged line" is shifted.
      // in this case, we grab the item on the index where parentLevelPathKey.at(-1) is pointing to -- from the draggedLineParentPathArr
      // and delete, and insert (essentially replace) the value of dropTargetLineLastPathItem + 2 (because +1 value was used to insert dragged item)

      // if the last item in the array was and/or, we need to get the 2nd to last item to check what the index of the "parent" was
      // e.g. and[1].or -> ["and", "1", "or"] << "1" is the parent (or)'s index
      const draggedLineParentIndex = ['and', 'or'].includes(draggedLineParentPathArr.at(-1)) && draggedLineParentPathArr.at(-2)
        ? getPureNumberFromPartialPath(draggedLineParentPathArr.at(-2))
        : getPureNumberFromPartialPath(draggedLineParentPathArr.at(-1));
      const indexOfTheReorderedItem = Number(dropTargetLineLastPathItem) + 1;

      if (
        (draggedLineParentIndex >= indexOfTheReorderedItem
          && draggedLineParentPathArr === dropTargetLineParentPathArr)
        || !draggedLineParentRule
        || !isEqual(originalDraggedLineParentRule, draggedLineParentRule)
      ) {
        // we check if dragged line's parent was located on the same level as the drop target line's parent
        // by checking the length difference and want it to be exactly 2, if that's the case, this is where parent was updated as part of earlier execution
        // and also compare dragged line's parent's index vs. index of the newly reordered item, and if new item index is smaller than dragged line parent index,
        // we update the path below to point to the right parent rule
        if (draggedLineParentPathArr[dropTargetLineParentPathArr.length]) {
          let partialPath = draggedLineParentPathArr[dropTargetLineParentPathArr.length];
          if (isItemDroppedInTheFirstIndexOfGroup
            && draggedLineParentPathArr[dropTargetLineParentPathArr.length + 1]) {
            // if user dropped it to be the first item of a group or a scope of operator object, the path would end with  ...and|or, not containing [0] or [1].
            // so we need to account for that by setting the path to '0'
            partialPath = draggedLineParentPathArr[dropTargetLineParentPathArr.length + 1];
            // if partialPath was assigned as '0', we need to splice one index further
          }

          const newIndex = getPureNumberFromPartialPath(partialPath);
          if (!isNaN(newIndex)) {
            draggedLineParentPathArr.splice(
              isItemDroppedInTheFirstIndexOfGroup
                ? dropTargetLineParentPathArr.length + 1
                : dropTargetLineParentPathArr.length,
              1,
              `${newIndex + 1}`,
            );
          }

          const newParentPath = createStringOfPathArray(draggedLineParentPathArr);
          draggedLineParentRule = getProperty(
            sourceActionRules, newParentPath) as BooleanRuleArray;
        }
      }
      // otherwise, we don't change parent rule/path
    }

    const droppedLineParentRule = getProperty(
      sourceActionRules, dropTargetLineParentPath) as IndexedRule | BooleanRule | BooleanRuleArray;
    const operatorOfDroppedLineParentRule = getOperatorUsedInRuleObject({
      rule: droppedLineParentRule as IndexedRule | BooleanRule,
    });

    // we need to update parent ruleset of the dragged line
    if ((
      draggedLineParentPath === dropTargetLineParentPath
      || (
        originalDropTargetLineEndsWithOperator
        && operatorOfDroppedLineParentRule
        && isEqual(droppedLineParentRule[operatorOfDroppedLineParentRule], draggedLineParentRule)
      )
      // this is to check if the droppedLineParentRule points to an object of "and/or": [array] and draggedLineParentRule points to that [array].
      // in that case, it's technically going to the same spot, despite the difference in those 2 variables due to path difference when an item is reordered to be the first item of a group scope.
    )
    && (
      (
        draggedLineParentPath === dropTargetLineParentPath
        && dropTargetLineLastPathItem <= draggedLineLastPathItem
      )
      || isItemDroppedInTheFirstIndexOfGroup
    )) {
      // this means the swap (reorder) happened on the same parent level
      draggedLineParentRule.splice(Number(draggedLineLastPathItem) + 1, 1);
    } else {
      draggedLineParentRule.splice(Number(draggedLineLastPathItem), 1);
    }

    if (draggedLineParentRule.length < 1) {
      // this rule is no longer valid (empty) and we need to disband this group
      let draggedLineParentParentPathArr = draggedLineParentPathArr;
      if (['and', 'or'].includes(draggedLineParentPathArr.at(-1))) {
        // if the last item in the array was and/or, we need to trim the last 2 from the array to get to the parent of parent
        // e.g. and[1].or -> ["and", "1", "or"] << "and" is where the parent is
        draggedLineParentParentPathArr = draggedLineParentPathArr.slice(
          0, draggedLineParentPathArr.length - 2);
      }

      const draggedLineParentParentPath = createStringOfPathArray(draggedLineParentParentPathArr);
      const draggedLineParentLastPathItem = getPureNumberFromPartialPath(
        draggedLineParentPathArr.at(-2));
      // get the parent of parent
      const draggedLineParentParentRule = getProperty(
        sourceActionRules, draggedLineParentParentPath) as BooleanRuleArray;

      // disband group in the parent of parent level
      const updatedParentParentRule = [
        ...draggedLineParentParentRule.slice(
          0, Number(draggedLineParentLastPathItem)),
        ...draggedLineParentRule,
        ...draggedLineParentParentRule.slice(Number(draggedLineParentLastPathItem) + 1), // we skip the item that's now disbanded and going away
      ];

      setProperty(
        sourceActionRules,
        draggedLineParentParentPath,
        updatedParentParentRule,
      );
    }

    return sourceActionRules;
  }

  return sourceActionRules;
}
